import {
  Drawer,
  Stack,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from '@mui/material'
import { TFunction } from 'i18next'
import { Button, IconButton } from 'utoniq-ui'

import { useBrowserLink } from '@/components/lib/BrowserLink'
import { StorageImage, useTranslation } from '@/core'
import { Cell, CellFrame } from '@/core/architecture'
import { getPlatform } from '@/core/native/app'
import { getHelpUrl } from '@/lib/helpUrl'

export type CryptoWalletConnectDrawerProps = {
  open: boolean
  onClose: () => void
  connecting: boolean
  onConnectWallet: (walletType: WalletType) => void
  sx?: SxProps<Theme>
}

export type WalletType = 'web3auth' | 'metamask'

export const CryptoWalletConnectDrawer: Cell<CryptoWalletConnectDrawerProps> =
  ({ open, onClose, connecting, onConnectWallet, sx }) => {
    const { t } = useTranslation('wallet')
    const { zIndex } = useTheme()
    const isWeb = getPlatform() === 'web'

    return (
      <Drawer
        open={open}
        onClose={onClose}
        sx={{ zIndex: zIndex.modal + 1, ...sx }}
      >
        <CellFrame sx={{ bgcolor: 'grey.50' }}>
          <Stack spacing={2} sx={{ py: 4 }}>
            <Typography variant="body2" align="center">
              {t('nftConnectDescription')}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'error.main' }}
              align="center"
            >
              {t('nftConnectNote')}
            </Typography>
          </Stack>
          {isWeb && (
            <Web3AuthConnect
              t={t}
              onConnect={() => onConnectWallet('web3auth')}
              disabled={connecting}
            />
          )}

          <MetamaskConnect
            t={t}
            onConnect={() => onConnectWallet('metamask')}
            disabled={connecting}
          />

          <Stack justifyContent="center" alignItems="center" sx={{ py: 3 }}>
            <Button
              onClick={onClose}
              variant="text"
              fullWidth
              disabled={connecting}
            >
              <Typography variant="h6" color="primary">
                {t('cancel')}
              </Typography>
            </Button>
          </Stack>
        </CellFrame>
      </Drawer>
    )
  }

type MetamaskConnectProps = {
  onConnect: () => void
  disabled: boolean
  t: TFunction
}

const MetamaskConnect = ({ onConnect, disabled, t }: MetamaskConnectProps) => {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{ px: 5, py: 4 }}
    >
      <Stack direction="row" alignItems="center">
        <StorageImage
          target="common"
          src="images/nft/metaMask.png"
          width={35}
          height={35}
        />
        <Typography variant="subtitle1" sx={{ ml: 3 }}>
          MetaMask
        </Typography>
      </Stack>

      <Button
        color="primary"
        variant="contained"
        onClick={onConnect}
        disabled={disabled}
      >
        {t('toConnect')}
      </Button>
    </Stack>
  )
}

type Web3AuthConnectProps = {
  onConnect: () => void
  disabled: boolean
  t: TFunction
}

const Web3AuthConnect = ({ onConnect, disabled, t }: Web3AuthConnectProps) => {
  const { onClick } = useBrowserLink(getHelpUrl('createCryptoWallet'))
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{ px: 5, py: 4 }}
    >
      <Stack direction="row" alignItems="center">
        <Typography variant="subtitle1">
          {t('createCryptWalletInApp')}
        </Typography>
        <IconButton.Help onClick={onClick} />
      </Stack>
      <Button
        color="primary"
        variant="contained"
        onClick={onConnect}
        disabled={disabled}
      >
        {t('create')}
      </Button>
    </Stack>
  )
}
